import {produce} from 'immer';

import RemoveLayerConfirmationModal from 'samples/plate-maps/display-layer-controls/RemoveLayerConfirmationModal';
import {PlateMap} from 'samples/plate-maps/PlateMapConstants';
import legacyRunModal from 'ui-library/legacyRunModal';

/**
 * Returns true if any well has an entered value for the given parameter layer.
 *
 * Scratch-plate parameter layers store per-well values in `well.parameterLayers[layerId]`, which is
 * distinct from the `well.dataParameterTags` map that the procedure plate-map flow uses. Removing a
 * layer with entered values discards those values, so callers use this to decide whether to confirm
 * the deletion first.
 */
export function anyWellHasParameterLayerValue(plateMap: PlateMap, layerId: string): boolean {
  return plateMap.wells.some((well) => well.parameterLayers?.[layerId] != null);
}

/**
 * Wrapper around removing a scratch-plate parameter layer. Launches a confirmation modal if any well
 * already has an entered value for the layer (which would be discarded), otherwise deletes immediately.
 * @param plateMap The current state of the plate map
 * @param layerId The id of the parameter layer to maybe be deleted
 * @param onConfirmDelete Called when the user confirms the delete, or immediately if no confirmation is needed.
 * @param onLaunchModal Called immediately before the confirmation modal launches.
 */
export function confirmDeleteParameterLayer({
  plateMap,
  layerId,
  onConfirmDelete,
  onLaunchModal,
}: {
  plateMap: PlateMap;
  layerId: string;
  onConfirmDelete: (layerId: string) => void;
  onLaunchModal?: () => void;
}): void {
  if (anyWellHasParameterLayerValue(plateMap, layerId)) {
    onLaunchModal?.();
    legacyRunModal(RemoveLayerConfirmationModal, {
      layerNameText: 'parameter',
      onRequestSubmit: () => {
        onConfirmDelete(layerId);
      },
    });
  } else {
    onConfirmDelete(layerId);
  }
}

/**
 * Returns the indices of wells currently holding `value` for the given parameter layer.
 */
export function getWellIndicesWithParameterValue(
  plateMap: PlateMap,
  layerId: string,
  value: string | number
): Set<number> {
  const indices: Set<number> = new Set();
  plateMap.wells.forEach((well, index) => {
    if (well.parameterLayers?.[layerId] === value) {
      indices.add(index);
    }
  });
  return indices;
}

/**
 * Returns a copy of `plateMap` with `value` written into `well.parameterLayers[layerId]` for each
 * well in `wellIndices`. Pass `null` to clear the value from those wells.
 */
export function applyParameterValueToSelected(
  plateMap: PlateMap,
  layerId: string,
  value: string | number | null,
  wellIndices: Set<number>
): PlateMap {
  return produce(plateMap, (draft) => {
    for (let i = 0; i < draft.wells.length; i++) {
      if (!wellIndices.has(i)) {
        continue;
      }
      draft.wells[i].parameterLayers ??= {};
      draft.wells[i].parameterLayers![layerId] = value;
    }
  });
}

/**
 * Adds or removes one scratch-plate parameter layer at a time. Mirrors the procedure `toggleParameter`
 * helper but detects discarded values via `well.parameterLayers` rather than `well.dataParameterTags`.
 */
export function toggleParameterLayer({
  newSelectedIds,
  oldSelectedIds,
  plateMap,
  onConfirmAdd,
  onConfirmDelete,
  onLaunchConfirmDeleteModal,
}: {
  newSelectedIds: Array<string>;
  oldSelectedIds: Set<string>;
  plateMap: PlateMap;
  onConfirmAdd: (layerId: string) => void;
  onConfirmDelete: (layerId: string) => void;
  onLaunchConfirmDeleteModal: () => void;
}): void {
  // Ids are selected or deselected one at a time, so this is a removal iff the new selection is smaller.
  if (newSelectedIds.length < oldSelectedIds.size) {
    const newIdSet = new Set(newSelectedIds);
    const [removedLayerId] = [...oldSelectedIds].filter((id) => !newIdSet.has(id));
    confirmDeleteParameterLayer({
      plateMap,
      layerId: removedLayerId,
      onConfirmDelete,
      onLaunchModal: onLaunchConfirmDeleteModal,
    });
  } else {
    const [newId] = newSelectedIds.filter((id) => !oldSelectedIds.has(id));
    onConfirmAdd(newId);
  }
}
